import { useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { addActivity, updateActivity, removeActivity } from '../redux/activitiesSlice';
import { subscribeToActivities } from '../services/supabaseClient/activitiesService';

/**
 * Custom hook to sync activities in real-time across devices
 * @param {string} userId - Current user's ID
 * @param {boolean} enabled - Whether sync is enabled (default: true)
 */
export const useActivitySync = (userId, enabled = true) => {
  const dispatch = useDispatch();

  // Handle new activity inserted
  const handleInsert = useCallback(
    (newActivity) => {
      // eslint-disable-next-line no-console
      console.log('[useActivitySync] New activity received:', newActivity);
      dispatch(addActivity(newActivity));
    },
    [dispatch]
  );

  // Handle activity updated
  const handleUpdate = useCallback(
    (updatedActivity) => {
      // eslint-disable-next-line no-console
      console.log('[useActivitySync] Activity updated:', updatedActivity);
      dispatch(
        updateActivity({
          id: updatedActivity.id,
          updates: updatedActivity,
        })
      );
    },
    [dispatch]
  );

  // Handle activity deleted
  const handleDelete = useCallback(
    (deletedActivity) => {
      // eslint-disable-next-line no-console
      console.log('[useActivitySync] Activity deleted:', deletedActivity);
      dispatch(removeActivity(deletedActivity.id));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!userId || !enabled) {
      return;
    }

    // eslint-disable-next-line no-console
    console.log('[useActivitySync] Setting up real-time sync for user:', userId);

    // Subscribe to real-time changes
    const subscription = subscribeToActivities(userId, {
      onInsert: handleInsert,
      onUpdate: handleUpdate,
      onDelete: handleDelete,
    });

    // Cleanup on unmount
    return () => {
      if (subscription) {
        // eslint-disable-next-line no-console
        console.log('[useActivitySync] Cleaning up subscription for user:', userId);
        subscription.unsubscribe();
      }
    };
  }, [userId, enabled, handleInsert, handleUpdate, handleDelete]);

  return {
    syncEnabled: enabled && !!userId,
  };
};

export default useActivitySync;
